import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import TableComponent from 'components/common/TableComponent'
import MonitorCard from 'components/Dashboard/MonitorCard'
import PageLoadingState from 'components/common/PageLoadingState'
import WorkerTimelineModal from 'components/modals/WorkerTimelineModal'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'

// GET attendance summary (single day). The endpoint takes a `filterDate`, not a range.
const getAttendanceSummary = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/attendance-summary?filterDate=${filterDate}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching attendance summary:', error)
    throw error
  }
}

const formatHours = (value) => `${(Number(value) || 0).toFixed(2)} h`

const AttendanceByGroupTable = ({ attendanceBoard }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'groupName', label: 'Group Name' },
      { key: 'workersNumber', label: 'Workers' },
      { key: 'startTime', label: 'First Login', cell: (value) => value || '—' },
      { key: 'endTime', label: 'Last Activity', cell: (value) => value || '—' },
      { key: 'totalTime', label: 'Total work hours', cell: (value) => formatHours(value) },
      {
        key: 'columnTasks',
        label: 'Activities',
        cell: (value) => Array.isArray(value) && value.length > 0
          ? value.map(t => `${t.taskName} (${t.workerCount})`).join(', ')
          : '—'
      }
    ],
    rows: attendanceBoard
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersTimelineTable = ({ workersTimeline, onRowClick }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'workerId', label: 'Worker ID', cell: (value, row) => row.oldId || row.workerId },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'groupName', label: 'Group Name', cell: (value) => value || '—' },
      { key: 'entryTime', label: 'Clock-in', cell: (value) => value || '—' },
      { key: 'officialEndTime', label: 'Official End', cell: (value) => value || '—' },
      { key: 'totalHours', label: 'Total work hours', cell: (value) => formatHours(value) },
      { key: 'segments', label: 'Sectors', cell: (value) => (Array.isArray(value) ? value.length : 0) }
    ],
    rows: workersTimeline
  }
  return <TableComponent tableData={tableData} defaultSortColumn='workerName' onRowClick={onRowClick} />
}

const DailyAttendanceSummary = () => {
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [filterDate, setFilterDate] = useState(currentDate)
  const [data, setData] = useState({ attendanceBoard: [], workersTimeline: [], loading: true, error: null })
  const [selectedWorker, setSelectedWorker] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchData = async (dateToFetch) => {
    if (!user?.farmId) return
    try {
      setData(prev => ({ ...prev, loading: true, error: null }))
      const res = await getAttendanceSummary(user.farmId, dateToFetch)
      setData({
        attendanceBoard: res.attendanceBoard || [],
        workersTimeline: res.workersTimeline || [],
        loading: false,
        error: null
      })
    } catch (error) {
      setData(prev => ({ ...prev, loading: false, error: 'Failed to load attendance summary data' }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true
    fetchData(filterDate)
  }, [user?.farmId])

  useEffect(() => {
    if (!hasFetched.current) return
    fetchData(filterDate)
  }, [filterDate])

  const openTimeline = (row) => {
    setSelectedWorker(row)
    setIsModalOpen(true)
  }

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-green-600'>Daily Attendance Summary</h1>
          <p className='text-sm text-gray-600'>Attendance for {filterDate}</p>
        </div>
        <label className='flex items-center gap-2 text-sm text-gray-700'>
          <span>Date</span>
          <input
            type='date'
            value={filterDate}
            max={currentDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className='p-2 border rounded-md shadow-sm bg-white'
          />
        </label>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      {data.loading && <PageLoadingState message='Loading attendance summary...' rows={12} />}

      {!data.loading && data.error && <div className='text-red-500'>{data.error}</div>}

      {!data.loading && !data.error && (
        <>
          <MonitorCard title='Attendance by Group' date={filterDate} hasData={data.attendanceBoard.length > 0} color='green'>
            <AttendanceByGroupTable attendanceBoard={data.attendanceBoard} />
          </MonitorCard>

          <MonitorCard title='Workers' date={filterDate} hasData={data.workersTimeline.length > 0} color='green'>
            <p className='text-xs text-gray-500 mb-2'>Click a worker to see their timeline.</p>
            <WorkersTimelineTable workersTimeline={data.workersTimeline} onRowClick={openTimeline} />
          </MonitorCard>
        </>
      )}

      <WorkerTimelineModal
        isOpen={isModalOpen}
        worker={selectedWorker}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  )
}

export default DailyAttendanceSummary
